import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, X, Check, GripVertical } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { api } from '../services/api'
import Input from './Input'
import Button from './Button'

function SortableEntityItem({ entity, onRemove, getEntityTitle, readOnly }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entity.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing mr-1"
      >
        <GripVertical className="h-3 w-3" />
      </div>
      <span>{getEntityTitle(entity)}</span>
      {!readOnly && (
        <button
          type="button"
          onClick={() => onRemove(entity.id)}
          className="ml-2 text-indigo-600 hover:text-indigo-800"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  )
}

export default function EntitySelector({
  field,
  value = [],
  onChange,
  locale = 'en',
  readOnly = false,
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const entityType = field.entity_type || 'post'
  const multiple = field.multiple_selection !== false

  const getEntityEndpoint = (type) => {
    const pluralMap = {
      category: 'categories',
      post: 'posts',
      page: 'pages',
    }
    return pluralMap[type] || `${type}s`
  }

  const { data: entitiesRes, isLoading } = useQuery({
    queryKey: [entityType, 'selector', searchQuery, locale],
    queryFn: () => {
      const params = {
        search: searchQuery,
        per_page: 20,
        locale,
      }
      
      if (entityType === 'category') {
        params.active = true
      } else {
        params.published = true
      }
      
      return api
        .get(`/${getEntityEndpoint(entityType)}`, { params })
        .then((r) => r.data)
    },
    enabled: showDropdown,
  })

  const selectedEntities = Array.isArray(value) ? value : []

  const handleToggle = (entity) => {
    if (readOnly) return

    const currentValues = Array.isArray(value) ? value : []

    if (multiple) {
      const isSelected = currentValues.some((e) => e.id === entity.id)
      if (isSelected) {
        onChange(currentValues.filter((e) => e.id !== entity.id))
      } else {
        onChange([...currentValues, entity])
      }
    } else {
      onChange(entity)
      setShowDropdown(false)
    }
  }

  const handleRemove = (entityId) => {
    if (readOnly) return
    const currentValues = Array.isArray(value) ? value : []
    onChange(currentValues.filter((e) => e.id !== entityId))
  }

  const handleDragEnd = (event) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const currentValues = Array.isArray(value) ? value : []
    const oldIndex = currentValues.findIndex((e) => e.id === active.id)
    const newIndex = currentValues.findIndex((e) => e.id === over.id)

    onChange(arrayMove(currentValues, oldIndex, newIndex))
  }

  const isSelected = (entityId) => {
    const currentValues = Array.isArray(value) ? value : []
    return currentValues.some((e) => e.id === entityId)
  }

  const getEntityTitle = (entity) => {
    if (entityType === 'post' || entityType === 'page') {
      return (
        entity.translations?.find((t) => t.locale === locale)?.title ||
        entity.title ||
        'Untitled'
      )
    } else if (entityType === 'category') {
      return (
        entity.translations?.find((t) => t.locale === locale)?.name ||
        entity.name
      )
    }
    return entity.name || entity.slug
  }

  const entities = entitiesRes?.data || []

  if (readOnly) {
    return (
      <div className="flex flex-wrap gap-2">
        {selectedEntities.length === 0 ? (
          <span className="text-sm text-gray-500">No selection</span>
        ) : (
          selectedEntities.map((entity) => (
            <span
              key={entity.id}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800"
            >
              {getEntityTitle(entity)}
            </span>
          ))
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <div
          className="flex items-center space-x-2 border border-gray-300 rounded-md px-3 py-2 cursor-pointer bg-white"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <Search className="h-4 w-4 text-gray-400" />
          <span className="flex-1 text-sm text-gray-600">
            {selectedEntities.length > 0
              ? `${selectedEntities.length} ${entityType}(s) selected`
              : `Click to select ${entityType}s...`}
          </span>
        </div>

        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-hidden">
            <div className="p-2 border-b">
              <Input
                placeholder={`Search ${entityType}s...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
            <div className="max-h-80 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  Loading...
                </div>
              ) : entities.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  No {entityType}s found
                </div>
              ) : (
                entities.map((entity) => {
                  const selected = isSelected(entity.id)
                  return (
                    <div
                      key={entity.id}
                      onClick={() => handleToggle(entity)}
                      className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <span className="text-sm text-gray-900">
                        {getEntityTitle(entity)}
                      </span>
                      {selected && (
                        <Check className="h-4 w-4 text-indigo-600" />
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </div>
        )}
      </div>

      {selectedEntities.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={selectedEntities.map((e) => e.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-wrap gap-2">
              {selectedEntities.map((entity) => (
                <SortableEntityItem
                  key={entity.id}
                  entity={entity}
                  onRemove={handleRemove}
                  getEntityTitle={getEntityTitle}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  )
}
